import { urlComSessao } from "./sessao";
import type { Row, DashboardData } from "./types";

const BASE = "/api";

/**
 * Usuário logado nesta sessão. É enviado no header das operações de usuários
 * para o servidor validar que apenas administradores criam/editam/excluem.
 */
let sessionUser = "";
export function setSessionUser(u: string) {
  sessionUser = u ?? "";
}
const authHeaders = () => ({ "x-sercamp-usuario": sessionUser });
/** Login da sessão — para módulos com cliente próprio (ex.: src/trafos.ts). */
export function usuarioDaSessao(): string {
  return sessionUser;
}

export class ApiError extends Error {
  code: string;
  constructor(message: string, code = "ERROR") {
    super(message);
    this.code = code;
  }
}

async function handle(res: Response) {
  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    let code = "ERROR";
    try {
      const body = await res.json();
      // Alguns endpoints (sincronização, remoção da equipe) explicam a falha em
      // `mensagem`, não em `error` — sem isto a tela mostrava só "Erro 502" e o
      // motivo real ("não foi possível falar com o RH...") era descartado.
      if (body?.error) msg = body.error;
      else if (body?.mensagem) msg = body.mensagem;
      if (body?.code) code = body.code;
    } catch {
      /* ignora */
    }
    throw new ApiError(msg, code);
  }
  return res.json();
}

export const api = {
  /**
   * APROVAÇÃO DE AGENDAMENTO (aba do gestor). O Planejamento manda o checkpoint
   * para cá e é a aprovação que marca a agenda — ver server/aprovacoes-agenda.ts.
   */
  aprovacoesAgenda: {
    list: (status?: string): Promise<any[]> =>
      fetch(`${BASE}/aprovacoes-agenda${status ? `?status=${encodeURIComponent(status)}` : ""}`, {
        headers: authHeaders(),
      }).then(handle),

    aprovar: (id: number, observacao?: string): Promise<{ ok: boolean; alocacoes: number; mensagem: string }> =>
      fetch(`${BASE}/aprovacoes-agenda/${id}/aprovar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ observacao }),
      }).then(handle),

    reprovar: (id: number, motivo: string): Promise<{ ok: boolean; mensagem: string }> =>
      fetch(`${BASE}/aprovacoes-agenda/${id}/reprovar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ motivo }),
      }).then(handle),
  },

  list: (resource: string): Promise<Row[]> =>
    fetch(`${BASE}/${resource}`, { headers: authHeaders() }).then(handle),

  create: (resource: string, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  update: (resource: string, id: number, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  remove: (resource: string, id: number): Promise<{ ok: boolean }> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    }).then(handle),

  /**
   * AGENDA — limpeza em lote (a exclusão de UMA marcação usa `remove`).
   *
   * A contagem vem do servidor, e não do que está carregado na tela: a grade
   * mostra só o período visível e pode estar defasada em relação ao que o
   * Planejamento acabou de aprovar. O número do aviso precisa ser o de verdade.
   */
  agenda: {
    /** Quantas marcações o filtro pega, sem apagar nada. */
    previaLimpeza: (de?: string | null, ate?: string | null): Promise<ContagemAgenda> => {
      const sp = new URLSearchParams();
      if (de) sp.set("de", de);
      if (ate) sp.set("ate", ate);
      const qs = sp.toString();
      return fetch(`${BASE}/agenda/limpeza${qs ? `?${qs}` : ""}`, { headers: authHeaders() }).then(handle);
    },

    /** Apaga o período; sem período exige `tudo` explícito (ver server/agenda.ts). */
    limpar: (dados: { de?: string | null; ate?: string | null; tudo?: boolean }): Promise<ResultadoLimpeza> =>
      fetch(`${BASE}/agenda/limpeza`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),
  },

  dashboard: (): Promise<DashboardData> =>
    fetch(`${BASE}/dashboard`, { headers: authHeaders() }).then(handle),

  /**
   * Puxa o cadastro de colaboradores do RH/DP (fonte única do portal) para a
   * base deste sistema. Usado pelo botão "Sincronizar" da aba Equipe.
   */
  sincronizarColaboradores: (): Promise<ResultadoSyncColaboradores> =>
    fetch(`${BASE}/colaboradores/sincronizar`, {
      method: "POST",
      headers: authHeaders(),
    }).then(handle),

  /**
   * Envia ao RH/DP quem só existe neste sistema (cadastro anterior à
   * centralização) e amarra o vínculo. É o que destrava a Equipe quando a
   * sincronização vive dizendo "N sem correspondência no RH/DP".
   */
  promoverColaboradores: (): Promise<ResultadoPromocaoColaboradores> =>
    fetch(`${BASE}/colaboradores/promover`, {
      method: "POST",
      headers: authHeaders(),
    }).then(handle),

  /** Cadastro espelhado do RH/DP: aqui só se marcam as competências. */
  colaboradores: {
    /**
     * Tira UMA pessoa da lista da Equipe. Só sai quem já não está mais no
     * cadastro do RH/DP — quem continua lá voltaria na próxima sincronização,
     * e nesse caso o servidor recusa explicando o que fazer.
     */
    removerDaEquipe: (id: number): Promise<ResultadoRemocaoEquipe> =>
      fetch(`${BASE}/colaboradores/${id}/equipe`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),

    /** Remove de uma vez todos os que saíram do RH/DP (limpeza da lista). */
    limparForaDaFonte: (): Promise<ResultadoRemocaoEquipe> =>
      fetch(`${BASE}/colaboradores/limpar-fora-da-fonte`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),

    /**
     * Salva a marcação de competências. O servidor grava primeiro no cadastro
     * central (RH/DP) e só depois neste sistema — se o RH estiver fora do ar a
     * chamada falha e nada é alterado, para a marcação não se perder na
     * próxima sincronização.
     */
    salvarSkills: (
      id: number,
      skills: string,
    ): Promise<{ ok: boolean; skills: string | null; sincronizado: boolean; mensagem: string }> =>
      fetch(`${BASE}/colaboradores/${id}/skills`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ skills }),
      }).then(handle),
  },

  /**
   * Veículos que precisam de manutenção (vencida/próxima/em manutenção).
   * Sem argumento: todos (visão de gestão). Passe o colaborador_id do usuário
   * logado para trazer só os veículos sob a responsabilidade dele.
   */
  manutencaoVeiculos: (responsavel?: number | null): Promise<VeiculoManutencao[]> =>
    fetch(
      `${BASE}/manutencao-veiculos${responsavel != null ? `?responsavel=${responsavel}` : ""}`,
      { headers: authHeaders() },
    ).then(handle),

  /** Retorna dados + permissões do usuário. permissoes=null => sem restrição (vê tudo). */
  me: (usuario: string): Promise<{
    usuario: string;
    nome: string | null;
    cargo: string | null;
    isAdmin: boolean;
    /** Só administradores excluem registros (regra do Portal) — esconde as lixeiras. */
    podeExcluir: boolean;
    permissoes: string[] | null;
    colaborador_id: number | null;
  }> => fetch(`${BASE}/me?u=${encodeURIComponent(usuario)}`).then(handle),

  /** IA de treinamentos: sugestão por colaborador + conclusão (skill/certificado). */
  ia: {
    sugerirTreinamentos: (colaboradorId: number): Promise<SugestaoTreino> =>
      fetch(`${BASE}/colaboradores/${colaboradorId}/sugerir-treinamentos`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),

    aplicarTreinamentos: (colaboradorId: number, treinamentos: Row[]): Promise<{ ok: boolean; criados: number }> =>
      fetch(`${BASE}/colaboradores/${colaboradorId}/sugerir-treinamentos/aplicar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ treinamentos }),
      }).then(handle),

    concluirTreinamento: (treinamentoId: number, form: FormData): Promise<{ ok: boolean; certificado: string | null; skill_atualizada: string }> =>
      fetch(`${BASE}/treinamentos/${treinamentoId}/concluir`, {
        method: "POST",
        headers: authHeaders(),
        body: form,
      }).then(handle),
  },

  /** Treinamentos: anexar o certificado (salva na pasta local + envia ao DP). */
  treinamentos: {
    enviarCertificado: (
      treinamentoId: number,
      form: FormData,
    ): Promise<{
      ok: boolean;
      local: string | null;
      dp: { enviado: boolean; mensagem: string; colaborador_id?: number };
    }> =>
      fetch(`${BASE}/treinamentos/${treinamentoId}/certificado`, {
        method: "POST",
        headers: authHeaders(),
        body: form,
      }).then(handle),
  },

  /** Pasta de arquivos/certificados do colaborador. */
  arquivos: {
    list: (colaboradorId: number): Promise<ArquivoInfo[]> =>
      fetch(`${BASE}/colaboradores/${colaboradorId}/arquivos`, { headers: authHeaders() }).then(handle),

    upload: (colaboradorId: number, files: File[] | FileList): Promise<{ ok: boolean; salvos: string[] }> => {
      const fd = new FormData();
      for (const f of Array.from(files)) fd.append("file", f);
      return fetch(`${BASE}/colaboradores/${colaboradorId}/arquivos`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }).then(handle);
    },

    url: (colaboradorId: number, nome: string): string =>
      urlComSessao(`${BASE}/colaboradores/${colaboradorId}/arquivos/${encodeURIComponent(nome)}`),

    remove: (colaboradorId: number, nome: string): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/colaboradores/${colaboradorId}/arquivos/${encodeURIComponent(nome)}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /** Consulta de insumos e solicitação de separação ao Almoxarifado (via proxy HTTP). */
  almox: {
    listItens: (): Promise<ItemAlmox[]> =>
      fetch(`${BASE}/almoxarifado/itens`, { headers: authHeaders() }).then(handle),

    listSolicitacoes: (solicitante?: string): Promise<SolicitacaoAlmox[]> =>
      fetch(`${BASE}/almoxarifado/solicitacoes${solicitante ? `?solicitante=${encodeURIComponent(solicitante)}` : ""}`, { headers: authHeaders() }).then(handle),

    criarSolicitacao: (data: { item_id: number; quantidade: number; prioridade: string; pedido: string }): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/almoxarifado/solicitacoes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    /** Vários itens de uma vez — os insumos de uma liberação de equipamento. */
    criarSolicitacoesLote: (data: {
      itens: { item_id: number; quantidade: number }[];
      prioridade: string;
      os_numero?: string;
      cliente?: string;
      atividade?: string;
      observacao?: string;
    }): Promise<{ ok: boolean; criadas: number; falhas: { item_id: number; erro: string }[] }> =>
      fetch(`${BASE}/almoxarifado/solicitacoes-lote`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),
  },

  /** Ordens de serviço e status dos relatórios — leitura, vem do Relatórios. */
  relatorios: {
    /** Uma OS pelo número exato (preenchimento automático da liberação). */
    buscarOs: (numero: string): Promise<OrdemServico> =>
      fetch(`${BASE}/relatorios/os/${encodeURIComponent(numero)}`, { headers: authHeaders() }).then(handle),

    /** Busca por número da OS ou nome do cliente. */
    pesquisarOs: (q: string): Promise<{ itens: OrdemServico[] }> =>
      fetch(`${BASE}/relatorios/os?q=${encodeURIComponent(q)}`, { headers: authHeaders() }).then(handle),

    /** Painel: uma linha por OS concluída (pendente × enviado) + resumo. */
    status: (): Promise<{ itens: StatusRelatorio[]; resumo: ResumoRelatorios }> =>
      fetch(`${BASE}/relatorios/status`, { headers: authHeaders() }).then(handle),
  },

  /** Folgas: avaliar (gestor), solicitar + minhas (colaborador). */
  folgas: {
    list: (): Promise<Folga[]> =>
      fetch(`${BASE}/folgas`, { headers: authHeaders() }).then(handle),

    avaliar: (id: number, decisao: "Aprovada" | "Reprovada", obs?: string): Promise<Folga> =>
      fetch(`${BASE}/folgas/${id}/avaliar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ decisao, obs: obs ?? "" }),
      }).then(handle),

    /** Solicita uma folga para o colaborador vinculado ao login atual. */
    solicitar: (data: string, motivo: string): Promise<Folga> =>
      fetch(`${BASE}/folgas/solicitar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ data, motivo }),
      }).then(handle),

    /** Folgas do próprio colaborador (para acompanhar o status). */
    minhas: (): Promise<Folga[]> =>
      fetch(`${BASE}/folgas/minhas`, { headers: authHeaders() }).then(handle),
  },

  /** Certificado de calibração do equipamento (lê a data do PDF e guarda o anexo). */
  certificados: {
    /** Só LÊ o PDF e devolve o que encontrou — nada é gravado ainda. */
    ler: (equipamentoId: number, file: File, meses: number): Promise<LeituraCertificado> => {
      const fd = new FormData();
      fd.append("file", file);
      return fetch(`${BASE}/equipamentos/${equipamentoId}/certificado/ler?meses=${meses}`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }).then(handle);
    },

    /** Grava as datas no equipamento e, se houver, anexa o PDF. */
    salvar: (
      equipamentoId: number,
      dados: { data_calibracao: string; data_validade: string; numero?: string | null; lidoDoPdf?: boolean; file?: File | null },
    ): Promise<{ ok: boolean; certificado_id: number | null; mensagem: string }> => {
      const fd = new FormData();
      fd.append("data_calibracao", dados.data_calibracao);
      fd.append("data_validade", dados.data_validade);
      if (dados.numero) fd.append("numero", dados.numero);
      fd.append("lido_do_pdf", String(!!dados.lidoDoPdf));
      if (dados.file) fd.append("file", dados.file);
      return fetch(`${BASE}/equipamentos/${equipamentoId}/certificado`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }).then(handle);
    },

    list: (equipamentoId: number): Promise<CertificadoInfo[]> =>
      fetch(`${BASE}/equipamentos/${equipamentoId}/certificados`, { headers: authHeaders() }).then(handle),

    // Abrir o PDF é `equipamentosEnsaio.abrirCertificado` (fetch + blob): o
    // endereço cru num `<a href>` não leva o usuário da sessão e dava 401.

    remover: (certificadoId: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/equipamentos/certificados/${certificadoId}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /**
   * Documentos do veículo (CRLV, seguro...). Anexados aqui, aparecem para a
   * equipe no Operacional. Abrir/baixar é pelos links `url`/`url_baixar` que a
   * lista já devolve assinados — funcionam num link comum, inclusive no celular.
   */
  documentosVeiculo: {
    list: (veiculoId: number): Promise<DocumentoVeiculo[]> =>
      fetch(`${BASE}/veiculos/${veiculoId}/documentos`, { headers: authHeaders() }).then(handle),

    anexar: (
      veiculoId: number,
      dados: { file: File; tipo: string; validade?: string; descricao?: string },
    ): Promise<{ ok: boolean; id: number | null; mensagem: string }> => {
      const fd = new FormData();
      fd.append("file", dados.file);
      fd.append("tipo", dados.tipo);
      if (dados.validade) fd.append("validade", dados.validade);
      if (dados.descricao) fd.append("descricao", dados.descricao);
      return fetch(`${BASE}/veiculos/${veiculoId}/documentos`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }).then(handle);
    },

    remover: (docId: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/documentos-veiculo/${docId}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /** Inspeção de veículo (checklist pré-uso): listar + registrar + fotos. */
  inspecoes: {
    list: (): Promise<Inspecao[]> =>
      fetch(`${BASE}/inspecoes`, { headers: authHeaders() }).then(handle),

    create: (data: NovaInspecao): Promise<{ id: number; data: string; status: string }> =>
      fetch(`${BASE}/inspecoes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    /**
     * Fotos de UMA inspeção (data URL pronta para o <img>). Ficam fora da
     * listagem de propósito: são imagens, e a lista carregaria todas de uma vez.
     */
    fotos: (id: number): Promise<FotoInspecao[]> =>
      fetch(`${BASE}/inspecoes/${id}/fotos`, { headers: authHeaders() }).then(handle),
  },

  /* ==================================================================
   *  EQUIPAMENTOS DE ENSAIO — bloco IDÊNTICO no Laboratório e na Gestão da
   *  Operação (a tela components/EquipamentosEnsaioView.tsx é o mesmo
   *  arquivo nos dois). Copie junto os tipos e o `pedirEq`, lá no fim deste
   *  arquivo. O dono dos dados é a Gestão da Operação; no Laboratório o
   *  servidor só repassa (server/equipamentos-ensaio.ts).
   *
   *  Mesmas regras de bancada das seringas: `duplicada`, `desconhecida` e
   *  `semCodigo` da bipagem, `divergencia` da confirmação e `pendencia` do
   *  encerramento NÃO viram exceção — a tela mostra em amarelo. E o conflito
   *  de nº de série / código no cadastro volta como resultado (com o
   *  equipamento que já existe), porque a tela oferece o que fazer com ele.
   * ================================================================== */
  equipamentosEnsaio: {
    resumo: (): Promise<ResumoEquipEnsaio> => pedirEq("resumo"),

    estoque: (
      filtros: { q?: string; situacao?: string; calibracao?: string; semCodigo?: boolean } = {},
    ): Promise<{ equipamentos: EquipEnsaio[]; total: number }> => {
      const p = new URLSearchParams();
      if (filtros.q) p.set("q", filtros.q);
      if (filtros.situacao) p.set("situacao", filtros.situacao);
      if (filtros.calibracao) p.set("calibracao", filtros.calibracao);
      if (filtros.semCodigo) p.set("semCodigo", "1");
      return pedirEq(`estoque?${p}`);
    },

    /** Um equipamento com todos os certificados dele. */
    equipamento: (id: number): Promise<{ equipamento: EquipEnsaio; certificados: CertificadoEquip[] }> =>
      pedirEq(`estoque/${id}`),

    /** Só a PRÉVIA do próximo código livre — o de verdade sai na hora de gravar. */
    proximoCodigo: (prefixo = "EQ-", signal?: AbortSignal): Promise<{ prefixo: string; codigo: string }> =>
      pedirEq(`proximo-codigo?prefixo=${encodeURIComponent(prefixo)}`, { signal }),

    /** Não lança em SERIE_DUPLICADA / CODIGO_DUPLICADO: devolve o equipamento que já existe. */
    cadastrar: async (dados: {
      nome: string;
      fabricante: string;
      modelo: string;
      numero_serie: string;
      codigo_barras?: string;
      gerar_codigo?: boolean;
      prefixo?: string;
      observacao?: string;
      origem?: "leitor" | "camera";
    }): Promise<ResultadoSalvarEquip> =>
      resultadoSalvar(await pedirEq("cadastrar", { metodo: "POST", json: dados, tolera: ehConflitoCadastro })),

    /** Só as chaves PRESENTES mudam (ver o contrato). Conflitos voltam como resultado. */
    atualizar: async (id: number, dados: Record<string, unknown>): Promise<ResultadoSalvarEquip> =>
      resultadoSalvar(await pedirEq(`estoque/${id}`, { metodo: "PUT", json: dados, tolera: ehConflitoCadastro })),

    darBaixa: (id: number, motivo: string): Promise<{ ok: boolean; equipamento?: EquipEnsaio }> =>
      pedirEq(`estoque/${id}/baixa`, { metodo: "POST", json: { motivo } }),

    voltarDaBaixa: (id: number): Promise<{ ok: boolean; equipamento?: EquipEnsaio }> =>
      pedirEq(`estoque/${id}/voltar-da-baixa`, { metodo: "POST", json: {} }),

    /** Dá código novo (prefixo EQ-) a quem AINDA não tem — até 100 de uma vez. */
    gerarCodigos: (ids: number[]): Promise<ResultadoGerarCodigos> =>
      pedirEq("gerar-codigos", { metodo: "POST", json: { ids } }),

    /** Grava o código que o leitor acabou de ler num equipamento que não tinha código. */
    codigoDoLeitor: (id: number, codigo: string): Promise<{ ok: boolean; equipamento?: EquipEnsaio }> =>
      pedirEq(`estoque/${id}/codigo-do-leitor`, { metodo: "POST", json: { codigo } }),

    remover: (id: number): Promise<{ ok: boolean }> => pedirEq(`estoque/${id}`, { metodo: "DELETE" }),

    /** O "desfazer" de quem acabou de cadastrar (só o próprio autor, em 24 h, sem uso). */
    desfazerCadastro: (id: number, codigo_barras: string): Promise<{ ok: boolean }> =>
      pedirEq("desfazer-cadastro", { metodo: "POST", json: { id, codigo_barras } }),

    /** Só LÊ o PDF (datas, nº, série…) — nada é gravado. */
    lerCertificado: (file: File, meses: number): Promise<LeituraCertificadoEquip> => {
      const fd = new FormData();
      fd.append("file", file);
      return pedirEq(`certificado/ler?meses=${meses}`, { metodo: "POST", form: fd });
    },

    /** Anexa o certificado (PDF opcional: só as datas também vale — certificado em papel). */
    salvarCertificado: (
      id: number,
      dados: {
        file?: File | null;
        data_calibracao: string;
        data_validade: string;
        numero?: string;
        lido_do_pdf?: boolean;
      },
    ): Promise<{ ok: boolean; certificado_id: number | null; mensagem?: string }> => {
      const fd = new FormData();
      if (dados.file) fd.append("file", dados.file);
      fd.append("data_calibracao", dados.data_calibracao);
      fd.append("data_validade", dados.data_validade);
      fd.append("numero", dados.numero ?? "");
      fd.append("lido_do_pdf", String(!!dados.lido_do_pdf));
      return pedirEq(`estoque/${id}/certificado`, { metodo: "POST", form: fd });
    },

    certificados: (id: number): Promise<{ certificados: CertificadoEquip[] }> =>
      pedirEq(`estoque/${id}/certificados`),

    /**
     * Abre o PDF numa aba nova. NUNCA por `<a href>`: o navegador não manda o
     * cabeçalho do usuário num link, e o servidor responde 401. A janela abre
     * ANTES da espera (aberta depois, o navegador a trata como pop-up não
     * pedido e bloqueia) e recebe o arquivo já baixado, como blob.
     */
    abrirCertificado: async (certId: number): Promise<void> => {
      const janela = window.open("", "_blank");
      if (!janela) {
        throw new ApiError(
          "O navegador bloqueou a janela. Libere os pop-ups para este site e tente de novo.",
          "POPUP",
        );
      }
      try {
        janela.document.title = "Abrindo o certificado…";
        janela.document.body.textContent = "Abrindo o certificado…";
      } catch {
        /* janela de outra origem: só não mostra o "abrindo" */
      }
      try {
        const res = await fetch(`${BASE}/equipamentos-ensaio/certificados/${certId}/arquivo`, {
          headers: authHeaders(),
        });
        if (!res.ok) {
          const corpo = await res.json().catch(() => null);
          throw new ApiError(corpo?.error ?? `Erro ${res.status} ao abrir o certificado.`, corpo?.code ?? "ERROR");
        }
        const bytes = await res.arrayBuffer();
        const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
        janela.location.href = url;
        // Revogar na hora mataria o PDF antes de a aba terminar de carregar.
        setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
      } catch (err) {
        janela.close();
        if (err instanceof ApiError) throw err;
        throw new ApiError("Não foi possível baixar o certificado — tente de novo.", "REDE");
      }
    },

    removerCertificado: (certId: number): Promise<{ ok: boolean }> =>
      pedirEq(`certificados/${certId}`, { metodo: "DELETE" }),

    clientes: (q: string, signal?: AbortSignal): Promise<{ clientes: ClienteEquip[] }> =>
      pedirEq(`clientes?q=${encodeURIComponent(q)}`, { signal }),

    laboratoriosExternos: (): Promise<{ laboratorios: string[] }> => pedirEq("laboratorios-externos"),

    saidas: (filtros: { q?: string; status?: string } = {}): Promise<{ saidas: SaidaEquip[]; total: number }> => {
      const p = new URLSearchParams();
      if (filtros.q) p.set("q", filtros.q);
      if (filtros.status) p.set("status", filtros.status);
      return pedirEq(`saidas?${p}`);
    },

    saida: (id: number): Promise<DetalheSaidaEquip> => pedirEq(`saidas/${id}`),

    criarSaida: (dados: Record<string, unknown>): Promise<{ ok: boolean; saida: SaidaEquip }> =>
      pedirEq("saidas", { metodo: "POST", json: dados }),

    atualizarSaida: (id: number, dados: Record<string, unknown>): Promise<{ ok: boolean; saida: SaidaEquip }> =>
      pedirEq(`saidas/${id}`, { metodo: "PUT", json: dados }),

    removerSaida: (id: number): Promise<{ ok: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}`, { metodo: "DELETE" }),

    /** Não lança em `duplicada`, `desconhecida` nem `semCodigo` (ver o topo do bloco). */
    biparSaida: (id: number, codigo: string, origem: "leitor" | "camera" = "leitor"): Promise<ResultadoBipagemEquip> =>
      pedirEq(`saidas/${id}/bipar`, {
        metodo: "POST",
        json: { codigo, origem },
        tolera: (c) => !!(c?.duplicada || c?.desconhecida || c?.semCodigo),
      }),

    removerItemSaida: (id: number, itemId: number): Promise<{ ok: boolean }> =>
      pedirEq(`saidas/${id}/itens/${itemId}`, { metodo: "DELETE" }),

    /** Com divergência, o servidor pede confirmação (409 com `divergencia`, que não lança). */
    confirmarSaida: (
      id: number,
      forcar = false,
    ): Promise<{ ok: boolean; divergencia?: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/confirmar`, {
        metodo: "POST",
        json: { forcar },
        tolera: (c) => !!c?.divergencia,
      }),

    retornar: (id: number, codigo: string, origem: "leitor" | "camera" = "leitor"): Promise<ResultadoBipagemEquip> =>
      pedirEq(`saidas/${id}/retornar`, {
        metodo: "POST",
        json: { codigo, origem },
        tolera: (c) => !!c?.duplicada,
      }),

    desfazerRetorno: (id: number, itemId: number): Promise<{ ok: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/itens/${itemId}/desfazer-retorno`, { metodo: "POST", json: {} }),

    /** Faltando equipamento, o servidor pede confirmação (409 com `pendencia`, que não lança). */
    concluirSaida: (
      id: number,
      forcar = false,
      motivo?: string,
    ): Promise<{ ok: boolean; pendencia?: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/concluir`, {
        metodo: "POST",
        json: { forcar, motivo },
        tolera: (c) => !!c?.pendencia,
      }),

    /*
     * CORREÇÃO DE SAÍDA JÁ CONFIRMADA (quem pode: permissão "Corrigir saída de
     * equipamento" no Laboratório; administrador na Gestão). Toda correção
     * pede o motivo e fica registrada na saída.
     */
    corrigirDados: (
      id: number,
      dados: Record<string, unknown>,
      motivo: string,
    ): Promise<{ ok: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/corrigir`, { metodo: "PUT", json: { ...dados, motivo } }),

    /** Troca um equipamento da lista pelo que de fato foi (`aindaFora`: está com a equipe). */
    corrigirTrocar: (
      id: number,
      itemId: number,
      codigo: string,
      aindaFora: boolean,
      motivo: string,
    ): Promise<{ ok: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/corrigir/trocar`, {
        metodo: "POST",
        json: { item_id: itemId, codigo, ainda_fora: aindaFora, motivo },
      }),

    corrigirTirar: (id: number, itemId: number, motivo: string): Promise<{ ok: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/corrigir/tirar`, { metodo: "POST", json: { item_id: itemId, motivo } }),

    corrigirIncluir: (
      id: number,
      codigo: string,
      aindaFora: boolean,
      motivo: string,
    ): Promise<{ ok: boolean; mensagem?: string }> =>
      pedirEq(`saidas/${id}/corrigir/incluir`, {
        metodo: "POST",
        json: { codigo, ainda_fora: aindaFora, motivo },
      }),
  },
};

/** Resultado do espelhamento do cadastro de colaboradores (RH/DP → aqui). */
export interface ResultadoSyncColaboradores {
  ok: boolean;
  criados: number;
  atualizados: number;
  ignorados: number;
  somenteLocais: number;
  total: number;
  fonte: string | null;
  mensagem: string;
  /**
   * Quem o RH/DP tem mas NÃO é equipe de campo, com o motivo. Vem separado
   * da `mensagem` de propósito: a tela mostra só a contagem e abre a lista
   * quando o usuário pede (ver CrudView).
   */
  naoSaoDeCampo?: { nome: string; motivo: string }[];
  /** Quem está na lista daqui mas não existe (mais) no RH/DP. */
  foraDaFonte?: { id: number; nome: string | null; cargo: string | null; rh_id: number | null }[];
}

/** Retorno de "remover da equipe" (um) e de "limpar quem saiu do RH/DP" (todos). */
export interface ResultadoRemocaoEquipe {
  ok: boolean;
  removidos: number;
  nomes: string[];
  mensagem: string;
}

/** Retorno de "Enviar ao RH/DP" (adoção dos cadastros que só existem aqui). */
export interface ResultadoPromocaoColaboradores {
  ok: boolean;
  enviados: number;
  criadosNoRh: number;
  jaExistiam: number;
  vinculados: number;
  mensagem: string;
}

/** Veículo que precisa de manutenção (retorno de /manutencao-veiculos). */
export interface VeiculoManutencao {
  id: number;
  placa: string | null;
  modelo: string | null;
  colaborador_id: number | null;
  colaborador_nome: string | null;
  proxima_revisao: string | null;
  data_agendada: string | null;
  situacao_manutencao: string; // Manutenção vencida | Revisão próxima | Em manutenção
}

export interface SugestaoTreino {
  colaborador: { id: number; nome: string; cargo: string | null };
  diagnostico: string;
  fonte: "ia" | "padrao";
  treinamentos: { titulo: string; tipo: string; carga_horaria: number; prioridade: string; justificativa: string }[];
}

export interface ArquivoInfo {
  nome: string;
  tamanho: number;
  enviado_em: string;
  ext: string;
}

export interface ItemAlmox {
  id: number;
  codigo: string;
  descricao: string;
  categoria: string;
  unidade: string;
  localizacao: string;
  saldo: number;
  estoque_minimo: number;
  situacao: string;
  status: string;
}

export interface SolicitacaoAlmox {
  id: number;
  item_id: number;
  item_descricao?: string;
  item_codigo?: string;
  pedido: string;
  solicitante: string;
  quantidade: number;
  data_solicitacao: string;
  data_conclusao: string | null;
  prioridade: string;
  status: string;
}

/** Ordem de serviço, como o sistema de Relatórios (dono do dado) devolve. */
export interface OrdemServico {
  id: string;
  numero: string;
  cliente: string;
  responsavel: string;
  equipe: string[];
  equipamento: string | null;
  tipoServico: string;
  local: string;
  inicio: string; // AAAA-MM-DD
  fim: string; // AAAA-MM-DD
  status: string; // ativa | concluida | cancelada
}

/** Uma OS concluída no painel de relatórios: já enviou o relatório final? */
export interface StatusRelatorio {
  id: string;
  numero: string;
  cliente: string;
  equipamento: string | null;
  tipoServico: string;
  fimServico: string;
  finalizadaEm: string;
  temAnalise: boolean;
  prazoDias: number;
  prazoEnvio: string;
  enviado: boolean;
  enviadoEm: string | null;
  diasRestantes: number | null; // null quando já foi enviado
  atrasado: boolean;
}

export interface ResumoRelatorios {
  total: number;
  enviados: number;
  pendentes: number;
  atrasados: number;
  venceHoje: number;
}

/**
 * Uma resposta do checklist de inspeção ("pergunta de selecionar").
 *
 * Atenção ao vocabulário: o app do colaborador (quem preenche hoje) grava
 * "Conformidade" / "Não conformidade" / "Não se aplica"; registros antigos
 * feitos por aqui usam "Conforme" / "Não conforme" / "N/A". A tabela é a mesma,
 * então o relatório precisa reconhecer os dois.
 */
export interface RespostaInspecao {
  item: string;
  resposta: string;
  obs?: string;
}

/** O que o sistema conseguiu ler do PDF do certificado de calibração. */
export interface LeituraCertificado {
  ok: boolean;
  data_calibracao: string | null; // AAAA-MM-DD
  data_validade: string | null; // sugerida = calibração + N meses
  numero_certificado: string | null;
  numero_serie: string | null;
  modelo: string | null;
  fabricante: string | null;
  equipamento: string | null;
  datas_encontradas: string[];
  aviso: string | null;
  nome_arquivo: string;
  tamanho: number;
}

/** Certificado já anexado a um equipamento. */
export interface CertificadoInfo {
  id: number;
  nome_arquivo: string;
  mime: string;
  numero: string | null;
  data_calibracao: string | null;
  data_validade: string | null;
  lido_do_pdf: boolean;
  enviado_por: string | null;
  criado_em: string;
  tamanho_base64: number;
}

/** Documento anexado a um veículo (sem o arquivo; abrir/baixar pelos links). */
export interface DocumentoVeiculo {
  id: number;
  veiculo_id: number;
  tipo: string;
  descricao: string | null;
  validade: string | null;
  nome_arquivo: string;
  mime: string;
  tamanho: number;
  enviado_por: string | null;
  criado_em: string;
  url: string;
  url_baixar: string;
}

/** Uma foto tirada em um item do checklist (já como data URL). */
export interface FotoInspecao {
  id: number;
  item_index: number;
  item: string | null;
  url: string;
}

/** Payload de criação de uma inspeção de veículo. */
export interface NovaInspecao {
  veiculo_id?: number | null;
  placa?: string;
  modelo?: string;
  km?: number | null;
  data?: string; // YYYY-MM-DD
  respostas: RespostaInspecao[];
  observacoes?: string;
}

/** Inspeção de veículo registrada. */
export interface Inspecao {
  id: number;
  veiculo_id: number | null;
  colaborador_id: number | null;
  colaborador_nome: string | null;
  inspetor: string | null;
  placa: string | null;
  modelo: string | null;
  veiculo_placa: string | null;
  veiculo_modelo: string | null;
  km: number | null;
  data: string;
  respostas: RespostaInspecao[];
  observacoes: string | null;
  status: string; // Conforme | Com pendências
  created_at: string;
  /** Quantas fotos a inspeção tem (as imagens vêm em /inspecoes/:id/fotos). */
  fotos_total?: number;
}

/** Folga (alocação da agenda com cor "Folga") a ser avaliada. */
export interface Folga {
  id: number;
  colaborador_id: number | null;
  colaborador_nome: string | null;
  data: string; // YYYY-MM-DD
  servico: string | null;
  cor: string;
  folga_status: "Aprovada" | "Reprovada" | null; // null = pendente
  folga_obs: string | null;
  folga_avaliador: string | null;
  folga_avaliado_em: string | null;
}

/** Quantas marcações um período tem, quebradas por origem. */
export interface ContagemAgenda {
  total: number;
  /** Cor amarela = folga pedida pelo colaborador (some junto se apagar). */
  folgas: number;
  /** Vieram do Planejamento (têm atividade_id) — dias de OS. */
  planejamento: number;
}

/** Resposta da limpeza da agenda. */
export interface ResultadoLimpeza extends ContagemAgenda {
  ok: boolean;
  apagadas: number;
  mensagem?: string;
}

/* ======================================================================
 *  EQUIPAMENTOS DE ENSAIO — o "pedir" e os tipos do bloco
 *  api.equipamentosEnsaio. IGUAIS no Laboratório e na Gestão da Operação.
 * ==================================================================== */

interface PedidoEq {
  metodo?: "GET" | "POST" | "PUT" | "DELETE";
  json?: unknown;
  /** Multipart (PDF do certificado): o navegador põe o Content-Type com o boundary. */
  form?: FormData;
  signal?: AbortSignal;
  /** Respostas de erro que NÃO viram exceção — a tela trata como aviso. */
  tolera?: (corpo: any) => boolean;
}

/**
 * Uma chamada às rotas /api/equipamentos-ensaio/...
 *
 * Existe (em vez do `handle` de sempre) por causa do estado "indisponível" da
 * tela: os dados moram na Gestão da Operação, e quando ela está fora, ou
 * desatualizada, a resposta pode nem ser JSON. Cada falha vira um ApiError com
 * código — REDE (sem resposta), RESPOSTA_INVALIDA (veio HTML/lixo) ou o `code`
 * do servidor (OPERACIONAL_DOWN, OPERACIONAL_DESATUALIZADA, INTEGRACAO_CONFIG…)
 * — para a tela mostrar o aviso certo em vez de uma lista vazia que mente.
 */
async function pedirEq<T = any>(caminho: string, p: PedidoEq = {}): Promise<T> {
  const headers: Record<string, string> = { ...authHeaders() };
  let body: BodyInit | undefined;
  if (p.form) body = p.form;
  else if (p.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(p.json);
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}/equipamentos-ensaio/${caminho}`, {
      method: p.metodo ?? "GET",
      headers,
      body,
      signal: p.signal,
    });
  } catch (err) {
    // Busca cancelada de propósito (a pessoa continuou digitando): segue como está.
    if ((err as Error)?.name === "AbortError") throw err;
    throw new ApiError("Sem resposta do servidor — confira a conexão e tente de novo.", "REDE");
  }

  const texto = await res.text().catch(() => "");
  let corpo: any = null;
  let jsonValido = true;
  try {
    corpo = texto ? JSON.parse(texto) : null;
  } catch {
    jsonValido = false;
  }

  if (!jsonValido || (res.ok && corpo === null)) {
    throw new ApiError(
      `O servidor respondeu algo inesperado (${res.status}) — os equipamentos de ensaio não puderam ser lidos. Tente de novo em instantes.`,
      "RESPOSTA_INVALIDA",
    );
  }
  if (!res.ok) {
    if (corpo && p.tolera?.(corpo)) return corpo as T;
    throw new ApiError(corpo?.error ?? corpo?.mensagem ?? `Erro ${res.status}`, corpo?.code ?? "ERROR");
  }
  return corpo as T;
}

/** 409 de nº de série / código já usados, COM o equipamento que já tem — a tela decide o que fazer. */
function ehConflitoCadastro(c: any): boolean {
  return (c?.code === "SERIE_DUPLICADA" || c?.code === "CODIGO_DUPLICADO") && !!c?.existente;
}

function resultadoSalvar(c: any): ResultadoSalvarEquip {
  if (c && ehConflitoCadastro(c)) {
    return { ok: false, code: c.code, error: String(c.error ?? ""), existente: c.existente };
  }
  return { ok: true, equipamento: c.equipamento, mensagem: c.mensagem, aviso: c.aviso };
}

/** Os números da aba (painel, cartões, quadro do estoque). */
export interface ResumoEquipEnsaio {
  estoque: {
    emEstoque: number;
    emServico: number;
    comCliente: number;
    emLabExterno: number;
    faltantes: number;
    baixados: number;
    total: number;
    /** Cadastrados sem código de barras (os antigos, antes desta aba). */
    semCodigo: number;
  };
  calibracao: { vencidas: number; aVencer: number; semValidade: number };
  saidas: { emSeparacao: number; enviadas: number; concluidas: number };
  /** Quantos equipamentos estão fora AGORA esperando retorno. */
  aguardandoRetorno: number;
}

/** Um equipamento de ensaio, como as listas devolvem (cadastro + onde está). */
export interface EquipEnsaio {
  id: number;
  nome: string;
  /** A "Marca" da tela (a coluna se chama fabricante desde sempre). */
  fabricante: string | null;
  modelo: string | null;
  numero_serie: string | null;
  codigo_barras: string | null;
  tipo: string | null;
  observacao: string | null;
  /** A "Condição de uso" (Disponível, Em atividade, Em manutenção...). */
  autorizacao: string | null;
  data_calibracao: string | null;
  data_validade: string | null;
  /** Calculada: Bloqueado / Sem validade / Vencida / A vencer / Válida. */
  situacao_calibracao: string | null;
  /** Onde está: Em estoque, Em serviço, Com cliente, Em laboratório externo, Faltante, Baixado. */
  estoque_situacao: string;
  estoque_saida_id: number | null;
  estoque_atualizado_em: string | null;
  cadastrado_por: string | null;
  created_at: string | null;
  certificado_id: number | null;
  certificado_numero: string | null;
  destino: string | null;
  os_numero: string | null;
  cliente_nome: string | null;
  cliente_cnpj: string | null;
  laboratorio_externo: string | null;
  saida_data: string | null;
  saida_status: string | null;
  /** "Em uso · OS 1234" — OS aberta no Relatórios. */
  em_uso: string | null;
  /** OS de uma liberação em aberto (aba Liberações da Gestão da Operação). */
  liberacao_os: string | null;
}

/** O equipamento que já tem o nº de série (ou o código) que se tentou gravar. */
export interface EquipExistente {
  id: number;
  nome: string;
  fabricante: string | null;
  modelo: string | null;
  numero_serie: string | null;
  codigo_barras: string | null;
  estoque_situacao: string | null;
  em_uso: string | null;
}

export type ResultadoSalvarEquip =
  | { ok: true; equipamento: EquipEnsaio; mensagem?: string; aviso?: string }
  | {
      ok: false;
      code: "SERIE_DUPLICADA" | "CODIGO_DUPLICADO";
      error: string;
      existente: EquipExistente;
    };

export interface ResultadoGerarCodigos {
  ok: boolean;
  gerados: { id: number; nome: string; codigo_barras: string }[];
  ignorados: { id: number; motivo: string }[];
}

/** Um certificado anexado (sem o arquivo). */
export interface CertificadoEquip {
  id: number;
  nome_arquivo: string | null;
  numero: string | null;
  data_calibracao: string | null;
  data_validade: string | null;
  lido_do_pdf: boolean | null;
  enviado_por: string | null;
  criado_em: string | null;
  tamanho: number | null;
}

/** O que a leitura do PDF achou (ver sistemaOperacional/server/certificado-calibracao.ts). */
export interface LeituraCertificadoEquip {
  ok: boolean;
  data_calibracao: string | null;
  /** Sugerida = calibração + N meses (o certificado real não traz validade). */
  data_validade: string | null;
  numero_certificado: string | null;
  numero_serie: string | null;
  modelo: string | null;
  fabricante: string | null;
  equipamento: string | null;
  datas_encontradas: string[];
  aviso: string | null;
  nome_arquivo?: string;
  tamanho?: number;
}

/** Um lote de saída (é a lista do Retorno também). */
export interface SaidaEquip {
  id: number;
  /** 'Serviço' | 'Cliente' | 'Laboratório externo' */
  destino: string;
  os_numero: string | null;
  cliente_nome: string | null;
  cliente_cnpj: string | null;
  laboratorio_externo: string | null;
  data: string | null;
  quantidade_prevista: number;
  observacao: string | null;
  responsavel: string | null;
  /** 'Em separação' | 'Enviada' | 'Concluída' */
  status: string;
  origem_sistema: string | null;
  enviada_em: string | null;
  concluida_em: string | null;
  created_at: string | null;
  bipados?: number;
  retornados?: number;
}

/** Um equipamento bipado numa saída. */
export interface ItemSaidaEquip {
  id: number;
  equipamento_id: number | null;
  codigo: string;
  /** Retrato do cadastro na hora da bipagem. */
  equipamento_nome: string | null;
  numero_serie: string | null;
  fabricante: string | null;
  modelo: string | null;
  estoque_situacao: string | null;
  bipado_por: string | null;
  bipado_em: string | null;
  origem: string | null;
  retornado_em: string | null;
  retornado_por: string | null;
  retorno_origem: string | null;
  /** Calculados agora (calibração vencida, em manutenção...) — nada disso bloqueia. */
  alertas: string[];
}

export interface DetalheSaidaEquip {
  saida: SaidaEquip;
  itens: ItemSaidaEquip[];
  bipados: number;
  retornados: number;
  pendentes: number;
  previsto: number;
  /** Correções feitas depois de confirmada (a mais nova primeiro). */
  correcoes?: CorrecaoSaidaEquip[];
  /** Quem está vendo pode corrigir esta saída já confirmada (o servidor decide). */
  pode_corrigir?: boolean;
}

/** Uma correção numa saída já confirmada — fica registrada com o motivo. */
export interface CorrecaoSaidaEquip {
  id: number;
  /** "trocar" | "tirar" | "incluir" | "editar" */
  acao: string;
  detalhe: string;
  motivo: string;
  usuario: string | null;
  /** "laboratorio" | "operacional" — de qual sistema veio. */
  origem: string | null;
  criado_em: string;
}

/** Resposta das bipagens de saída e de retorno. */
export interface ResultadoBipagemEquip {
  ok: boolean;
  duplicada?: boolean;
  /** Só na saída: o código não é de nenhum equipamento. */
  desconhecida?: boolean;
  /** Só na saída: o código lido é o Nº DE SÉRIE de um equipamento ainda sem código. */
  semCodigo?: boolean;
  equipamento?: { id: number; nome: string; modelo: string | null; numero_serie: string | null };
  codigo?: string;
  item?: ItemSaidaEquip;
  mensagem?: string;
  bipados?: number;
  retornados?: number;
  pendentes?: number;
  previsto?: number;
  completo?: boolean;
  /** Só no retorno: o último voltou e a saída se encerrou sozinha. */
  fechada?: boolean;
  /** Só na saída: o que conferir (entrou mesmo assim). */
  alertas?: string[];
  /** Só no retorno: a condição de uso que continua (ex.: "Em manutenção"). */
  aviso?: string;
}

export interface ClienteEquip {
  nome: string;
  cnpj: string | null;
  fontes: string[];
}
